import { useState, useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../contexts/AuthContext'
import { AuthApiError } from '../services/authService'
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator'
import { ForgotPasswordModal } from '../components/ForgotPasswordModal'
import { RegistrationIncompleteModal } from '../components/RegistrationIncompleteModal'
import { useProfileCompletion } from '../hooks/useProfileCompletion'
import { env } from '../config/env'
import { isValidReturnUrl } from '../utils/urlValidation';

interface LoginProps {
  initialMode?: 'login' | 'signup'
}

const SESSION_EXPIRED_KEY = 'sessionExpiredMessage';

export function Login({ initialMode = 'login' }: LoginProps) {
  const [isRegisterMode, setIsRegisterMode] = useState(initialMode === 'signup')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notification, setNotification] = useState<string | null>(null)
  const [passwordsMatch, setPasswordsMatch] = useState(true)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [showRegistrationIncomplete, setShowRegistrationIncomplete] = useState(false)

  const { login, register, logout, isAuthenticated, isLoading: authLoading } = useAuth()
  const { isCheckingCompletion, isProfileComplete, requiresProfileCompletion, hasCheckedCompletion } = useProfileCompletion()
  const location = useLocation()

  // Check for session expired message and URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionExpired = urlParams.get('sessionExpired') === 'true';
    const returnUrl = urlParams.get('returnUrl');
    
    // Store return URL for redirect after login (with validation)
    if (returnUrl) {
      const decodedUrl = decodeURIComponent(returnUrl);
      if (isValidReturnUrl(decodedUrl)) {
        sessionStorage.setItem('returnUrl', decodedUrl);
      } else {
        console.warn('Invalid return URL detected and ignored:', decodedUrl);
      }
    }
    
    // Check for session expiration from URL or session storage
    if (sessionExpired) {
      setNotification('Your session has expired. Please log in again to continue.');
    } else {
      const sessionExpiredMessage = sessionStorage.getItem(SESSION_EXPIRED_KEY);
      if (sessionExpiredMessage) {
        setNotification(sessionExpiredMessage);
        sessionStorage.removeItem(SESSION_EXPIRED_KEY);
      }
    }
  }, []);

  // Clear any stored auth data when in signup mode to prevent auto-auth attempts
  // that could trigger REGISTRATION_INCOMPLETE errors for users without passwords
  useEffect(() => {
    if (isRegisterMode) {
      // Clear stored authentication data to prevent automatic login attempts
      // for users who may have incomplete registration data stored
      localStorage.removeItem('authToken')
      localStorage.removeItem('auth')
      sessionStorage.removeItem('authToken')
      
      // Force logout to clear any current authentication state
      if (isAuthenticated) {
        logout()
      }
    }
  }, [isRegisterMode, isAuthenticated, logout])

  // Redirect authenticated users based on profile completion
  // IMPORTANT: Wait for profile completion check to complete before redirecting
  if (!authLoading && isAuthenticated && !isCheckingCompletion && hasCheckedCompletion) {
    if (env.enableDebugLogging) {
      console.log('🔍 Login: Redirect logic triggered', {
        requiresProfileCompletion,
        isProfileComplete,
        currentPath: location.pathname,
        timestamp: new Date().toISOString()
      });
    }
    
    if (requiresProfileCompletion && !isProfileComplete) {
      if (env.enableDebugLogging) {
        console.log('🔄 Login: Redirecting to /profile (incomplete profile)');
      }
      return <Navigate to="/profile" replace />
    }
    if (env.enableDebugLogging) {
      console.log('🔄 Login: Redirecting to /dashboard (complete profile)');
    }
    return <Navigate to="/dashboard" replace />
  }

  // Show loading state while checking authentication or profile completion
  if (authLoading || (isAuthenticated && (isCheckingCompletion || !hasCheckedCompletion))) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  const handlePasswordConfirmChange = (value: string) => {
    setConfirmPassword(value)
    setPasswordsMatch(value === password || value === '')
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    setPasswordsMatch(confirmPassword === value || confirmPassword === '')
  }

  const isPasswordStrong = (pwd: string): boolean => {
    return pwd.length >= 8 &&
           /[A-Z]/.test(pwd) &&
           /[a-z]/.test(pwd) &&
           /\d/.test(pwd) &&
           /[@$!%*?&]/.test(pwd)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    // Validation for registration
    if (isRegisterMode) {
      if (!passwordsMatch) {
        setError('Passwords do not match')
        setIsLoading(false)
        return
      }
      
      if (!isPasswordStrong(password)) {
        setError('Password does not meet security requirements')
        setIsLoading(false)
        return
      }
    }
    
    try {
      if (isRegisterMode) {
        await register(firstName, lastName, email, password)
      } else {
        await login(email, password)
      }
      // Don't navigate here - let the redirect logic above handle it
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.code === 'REGISTRATION_INCOMPLETE') {
          setError(null)
          setShowRegistrationIncomplete(true)
        } else {
          setError(err.message)
        }
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const toggleMode = () => {
    setIsRegisterMode(!isRegisterMode)
    setError(null)
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setFirstName('')
    setLastName('')
    setPasswordsMatch(true)
  }

  return (
    <>
      <Helmet>
        <title>Contrarian OS | {isRegisterMode ? 'Sign Up' : 'Login'}</title>
      </Helmet>
      <div className="min-h-screen flex">
        {/* Left side - Branding */}
        <div className="hidden lg:flex lg:w-1/2 bg-primary items-center justify-center p-12">
          <div className="text-center text-primary-foreground">
            <div className="flex items-center justify-center space-x-3 mb-8">
              <img 
                src="/logo.svg" 
                alt="Contrarian OS" 
                className="h-12 w-auto filter brightness-0 invert"
              />
              <h1 className="text-3xl font-bold">
                Contrarian OS
              </h1>
            </div>
            <h2 className="text-xl font-semibold mb-4">
              Think Different. Think Contrarian.
            </h2>
            <p className="text-primary-foreground/80 max-w-md">
              A powerful platform designed to help you navigate complex decisions with contrarian thinking and focused analysis.
            </p>
          </div>
        </div>

        {/* Right side - Login/Register Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="text-center mb-8 lg:hidden">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <img 
                  src="/logo.svg" 
                  alt="Contrarian OS" 
                  className="h-8 w-auto"
                />
                <h1 className="text-2xl font-bold text-foreground">
                  Contrarian OS
                </h1>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {isRegisterMode ? 'Create your account' : 'Welcome back'}
              </h2>
              <p className="text-muted-foreground">
                {isRegisterMode 
                  ? 'Sign up to get started with Contrarian OS'
                  : 'Sign in to your account to continue'
                }
              </p>
            </div>

            {notification && (
              <div className="mb-6 p-4 bg-yellow-100 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">{notification}</p>
              </div>
            )}

            {error && (
              <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                <p className="text-sm text-destructive" dangerouslySetInnerHTML={{ __html: error }}></p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {isRegisterMode && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                      First Name
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required={isRegisterMode}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                      placeholder="First name"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                      Last Name
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required={isRegisterMode}
                      className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                      placeholder="Last name"
                    />
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground"
                  placeholder={isRegisterMode ? "Create a strong password" : "Enter your password"}
                />
                <PasswordStrengthIndicator 
                  password={password} 
                  isVisible={isRegisterMode && password.length > 0}
                />
              </div>

              {isRegisterMode && (
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => handlePasswordConfirmChange(e.target.value)}
                    required={isRegisterMode}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-background text-foreground ${
                      !passwordsMatch ? 'border-red-500' : 'border-border'
                    }`}
                    placeholder="Confirm your password"
                  />
                  {!passwordsMatch && confirmPassword.length > 0 && (
                    <p className="text-sm text-red-600 mt-1">Passwords do not match</p>
                  )}
                </div>
              )}

              {!isRegisterMode && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input type="checkbox" className="rounded border-border text-primary focus:ring-primary" />
                    <span className="ml-2 text-sm text-muted-foreground">Remember me</span>
                  </label>
                  <button 
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-primary hover:text-primary/80"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading || (isRegisterMode && (!passwordsMatch || !isPasswordStrong(password)))}
                className="w-full bg-primary text-primary-foreground py-2 px-4 rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isLoading 
                  ? (isRegisterMode ? 'Creating account...' : 'Signing in...') 
                  : (isRegisterMode ? 'Create account' : 'Sign in')
                }
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                {isRegisterMode ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button 
                  onClick={toggleMode}
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  {isRegisterMode ? 'Sign in' : 'Sign up'}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      <ForgotPasswordModal 
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />

      <RegistrationIncompleteModal 
        isOpen={showRegistrationIncomplete}
        onClose={() => setShowRegistrationIncomplete(false)}
      />
    </>
  )
} 
